import React, { useState, useEffect, useCallback } from "react";
import { Icon } from '@iconify/react/dist/iconify.js';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ReportDistrictSummary from './ReportDistrictSummary';
import ReportDepartmentSummary from './ReportDepartmentSummary';

const ReportActivityPage = () => {
  const [departmentData, setDepartmentData] = useState([]);
  const [districtData, setDistrictData] = useState([]);
  const [loading, setLoading] = useState(true);
  const API_BASE_URL = process.env.REACT_APP_BE_API_URL;

  // Memoized toast helper
  const showToast = useCallback((message, type = "success") => {
    const toastOptions = {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    };

    switch (type) {
      case "success":
        toast.success(message, toastOptions);
        break;
      case "error":
        toast.error(message, toastOptions);
        break;
      default:
        toast(message, toastOptions);
    }
  }, []);

  // Memoized fetch function
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      // Fetch department summary
      const deptResponse = await axios.post(
        `${API_BASE_URL}/api/plantation/pafs/department-summary`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (deptResponse.data.status) {
        setDepartmentData(deptResponse.data.data);
      }

      // Fetch district summary
      const districtResponse = await axios.post(
        `${API_BASE_URL}/api/plantation/pafs/only-public-district-summary`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (districtResponse.data.status) {
        setDistrictData(districtResponse.data.data);
      }

    } catch (error) {
      console.error("Error fetching data:", error);
      showToast("Failed to fetch data. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, showToast]); // Dependencies: API URL & toast

  // Initial load
  useEffect(() => {
    fetchData();
  }, [fetchData]); // Now includes fetchData

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="col-lg-12 col-md-12 col-sm-12">
      <ToastContainer />
      <div className="text-center">
        <h6 className="mb-20 text-2xl font-bold text-gray-800 flex items-center justify-center">
          <Icon icon="mdi:file-chart-outline" className="text-3xl mx-3 my-1" />
          Activity Report
        </h6>
      </div>

      <div className="col-xxl-12">
        <div className="card p-0 overflow-hidden position-relative radius-12 h-100">
          <div className="card-header bg-gradient-success py-16 px-24 bg-base border border-end-0 border-start-0 border-top-0">
            <h6 className="text-lg mb-0">Activity Report</h6>
          </div>
          <div className="card-body bg-gradient-success p-24 pt-10">
            <ul className="nav button-tab nav-pills mb-16" id="pills-tab-four" role="tablist">
              <li className="nav-item" role="presentation">
                <button
                  className="nav-link d-flex align-items-center gap-2 fw-semibold text-primary-light radius-4 px-16 py-10 active"
                  id="pills-button-icon-home-tab"
                  data-bs-toggle="pill"
                  data-bs-target="#pills-button-icon-home"
                  type="button"
                  role="tab"
                  aria-controls="pills-button-icon-home"
                  aria-selected="true"
                >
                  <Icon icon="solar:home-smile-angle-outline" className="text-xl" />
                  <span className="line-height-1">General Public</span>
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button
                  className="nav-link d-flex align-items-center gap-2 fw-semibold text-primary-light radius-4 px-16 py-10"
                  id="pills-button-icon-details-tab"
                  data-bs-toggle="pill"
                  data-bs-target="#pills-button-icon-details"
                  type="button"
                  role="tab"
                  aria-controls="pills-button-icon-details"
                  aria-selected="false"
                >
                  <Icon icon="hugeicons:folder-details" className="text-xl" />
                  <span className="line-height-1">Departmental</span>
                </button>
              </li>
            </ul>

            <div className="tab-content" id="pills-tab-fourContent">
              {/* General Public Tab */}
              <div
                className="tab-pane fade show active"
                id="pills-button-icon-home"
                role="tabpanel"
                aria-labelledby="pills-button-icon-home-tab"
                tabIndex={0}
              >
                <ReportDistrictSummary districtData={districtData} />
              </div>

              {/* Departmental Tab */}
              <div
                className="tab-pane fade"
                id="pills-button-icon-details"
                role="tabpanel"
                aria-labelledby="pills-button-icon-details-tab"
                tabIndex={0}
              >
                <ReportDepartmentSummary departmentData={departmentData} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportActivityPage;